import { createSlice } from '@reduxjs/toolkit';
import { buildPermissionMap } from 'utils/permissionUtils';
import { getApi } from 'services/api';
import { setUser } from './localSlice';

const initialState = {
	permissionMap:
		buildPermissionMap(JSON.parse(localStorage.getItem('user'))) || {},
};

const permissionSlice = createSlice({
	name: 'permissions',
	initialState,
	reducers: {
		setPermissions: (state, action) => {
			state.permissionMap = action.payload;
		},
		clearPermissions: (state) => {
			state.permissionMap = {};
		},
	},
});

export const { setPermissions, clearPermissions } = permissionSlice.actions;

/**
 * Re-read the CURRENT user's resolved permissions and rebuild the map.
 *
 * ★ THE MAP IS A LOGIN-TIME SNAPSHOT, AND NOTHING REFRESHED IT.
 * `buildPermissionMap` runs once at sign-in, and once more from
 * `localStorage['user']` on store init — then never again. So an admin changing
 * a user's role or clearing their override did not reach that user's live
 * session until they logged in again: the change was saved on the server and
 * enforced on every request, but the browser's cached map kept saying otherwise
 * ("my permission was updated but nothing changed").
 *
 * `GET /user/v2/view/:id` returns `roles[0].permissions` already resolved
 * (override-or-role) and subscription-filtered — the EXACT shape login bakes in
 * (see the login handler), so a refresh reproduces the login map, just fresh.
 *
 * ★ DRIVEN BY A SOCKET PUSH, not a timer or a focus listener. When an admin
 * changes a user's role or custom override, the server emits `permissions:changed`
 * to THAT user's socket room (`updateUser` → `emitToUser`); the client listener
 * dispatches this. So the change reaches the edited user's OWN live session —
 * whether the admin edited themselves or someone else — the moment it is saved,
 * with no re-login and no polling.
 *
 * ★ NEVER DISRUPTIVE. A failed or malformed response leaves the cached map
 * standing rather than blanking anyone's access. `force` skips the throttle,
 * which otherwise coalesces bursts to once every 30s.
 */
let lastRefreshAt = 0;
const REFRESH_MIN_INTERVAL_MS = 30 * 1000;

export const refreshPermissions =
	({ force = false } = {}) =>
	async (dispatch) => {
		try {
			const stored = JSON.parse(localStorage.getItem('user') || 'null');
			const userId = stored?._id;
			if (!userId) return;

			const now = Date.now();
			if (!force && now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) return;
			lastRefreshAt = now;

			const res = await getApi('api/user/v2/view/', userId);
			const fresh = res?.data;
			// A bad shape — an error object, a partial body — must NEVER wipe the
			// map. Only a real user document with roles is allowed to replace it.
			if (!fresh || !Array.isArray(fresh.roles)) return;

			const userData = {
				...stored,
				...fresh,
				roleName:
					fresh?.roles?.[0]?.roleName || fresh?.role || stored?.roleName || 'user',
			};

			localStorage.setItem('user', JSON.stringify(userData));
			dispatch(setUser(userData));
			dispatch(setPermissions(buildPermissionMap(userData)));
		} catch {
			// The cached map stands — a refresh must not break an active session.
		}
	};

export default permissionSlice.reducer;
